import type { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { dayText } from "./accounts";
import { todayIn } from "./pickup";
import {
  type AccountNote,
  type AccountSales,
  accountSales,
  addContact,
  addNote,
  changeContact,
  type Contact,
  contactLine,
  type ContactValues,
  errorText,
  missingRoute,
  NOTE_KIND_TEXT,
  type NoteKind,
  type PayeeValues,
  removeContact,
  setPayee,
  sortedNotes,
} from "./salesImport";

const NOTES_FIRST = 5;
const EMPTY_CONTACT: ContactValues = { name: "", role: "", phone: "", email: "" };

type Saving = { ok: boolean; text: string } | null;

/** Design 26.8: the contacts, the payee and the notes of the account. An older API shows nothing. */
export function AccountSalesSection({ accountId, user, timeZone }: { accountId: string; user: User; timeZone: string }) {
  const today = todayIn(timeZone);
  const [found, setFound] = useState<AccountSales | "missing" | "error" | undefined>(undefined);
  const [status, setStatus] = useState<Saving>(null);
  useEffect(() => {
    let live = true;
    const began = performance.now();
    user
      .getIdToken()
      .then((token) => accountSales(token, accountId))
      .then((sales) => {
        if (!live) return;
        console.info(`account sales: ${sales.contacts.length} contacts, ${sales.notes.length} notes in ${Math.round(performance.now() - began)} ms`);
        setFound(sales);
      })
      .catch((e) => {
        if (live) setFound(missingRoute(e) ? "missing" : "error");
      });
    return () => {
      live = false;
    };
  }, [user, accountId]);
  if (found === undefined || found === "missing") return null;
  if (found === "error") {
    return (
      <section aria-labelledby="account-people" data-testid="account-sales">
        <h2 id="account-people">Contacts, payee and notes</h2>
        <p role="alert" className="error" data-testid="account-sales-error">
          The contacts did not load. Reload the page to try again.
        </p>
      </section>
    );
  }
  const save = async (work: (token: string) => Promise<AccountSales>, done: string) => {
    setStatus(null);
    try {
      setFound(await work(await user.getIdToken()));
      setStatus({ ok: true, text: done });
      return true;
    } catch (e) {
      setStatus({ ok: false, text: errorText(e) });
      return false;
    }
  };
  const imported = found.contacts.some((c) => c.source === "import") || found.notes.some((n) => n.source === "import");
  return (
    <section aria-labelledby="account-people" data-testid="account-sales">
      <h2 id="account-people">Contacts, payee and notes</h2>
      {found.customer_since && <p data-testid="account-customer-since">Customer since {dayText(found.customer_since, true)}</p>}
      {imported && !found.import_locked && (
        <p className="sub" data-testid="account-sales-import-note">
          Some rows come from the sales import. The import writes them again until an administrator locks it.
        </p>
      )}
      <Contacts found={found} save={save} accountId={accountId} />
      <PayeePart found={found} save={save} accountId={accountId} />
      <Notes found={found} save={save} accountId={accountId} today={today} />
      <p role="status" data-testid="account-sales-status" className={status && !status.ok ? "error" : undefined}>
        {status?.text}
      </p>
    </section>
  );
}

type Save = (work: (token: string) => Promise<AccountSales>, done: string) => Promise<boolean>;

function ContactForm({
  prefix,
  start,
  label,
  onSubmit,
  onCancel,
}: {
  prefix: string;
  start: ContactValues;
  label: string;
  onSubmit: (v: ContactValues) => Promise<boolean>;
  onCancel?: () => void;
}) {
  const [values, setValues] = useState(start);
  const [saving, setSaving] = useState(false);
  const field = (key: keyof ContactValues, text: string, type = "text") => (
    <>
      <label htmlFor={`${prefix}-${key}`}>{text}</label>
      <input id={`${prefix}-${key}`} type={type} autoComplete="off" value={values[key]} onChange={(e) => setValues((old) => ({ ...old, [key]: e.target.value }))} />
    </>
  );
  return (
    <form
      data-testid="contact-form"
      noValidate
      onSubmit={async (e) => {
        e.preventDefault();
        setSaving(true);
        const ok = await onSubmit(values);
        setSaving(false);
        if (ok && !onCancel) setValues(EMPTY_CONTACT);
      }}
    >
      {field("name", "Name")}
      {field("role", "Role")}
      {field("phone", "Phone", "tel")}
      {field("email", "Email", "email")}
      <div className="actions">
        <button className="primary" data-testid="contact-save" disabled={saving}>
          {saving ? "Saving…" : label}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function Contacts({ found, save, accountId }: { found: AccountSales; save: Save; accountId: string }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const can = found.can_edit_contacts;
  const row = (contact: Contact) =>
    editing === contact.id ? (
      <li key={contact.id}>
        <ContactForm
          prefix={`contact-${contact.id}`}
          start={contact}
          label="Save the contact"
          onCancel={() => setEditing(null)}
          onSubmit={async (values) => {
            const ok = await save((t) => changeContact(t, accountId, contact.id, values), "The contact is saved.");
            if (ok) setEditing(null);
            return ok;
          }}
        />
      </li>
    ) : (
      <li key={contact.id} data-testid="contact-row" data-source={contact.source}>
        <strong>{contact.name || "No name"}</strong>
        {contactLine(contact) && <span className="sub"> · {contactLine(contact)}</span>}
        {contact.source === "import" && <span className="sub"> · from the sales import</span>}
        {can && (
          <>
            {" "}
            <button type="button" className="link" data-testid="contact-edit" onClick={() => setEditing(contact.id)}>
              Change
            </button>{" "}
            <button
              type="button"
              className="link"
              data-testid="contact-remove"
              onClick={() => save((t) => removeContact(t, accountId, contact.id), "The contact is removed.")}
            >
              Remove
            </button>
          </>
        )}
      </li>
    );
  return (
    <div data-testid="account-contacts">
      <h3>Contacts</h3>
      {found.contacts.length ? <ul className="contacts">{found.contacts.map(row)}</ul> : <p data-testid="account-no-contacts">No contacts.</p>}
      {can && !adding && (
        <p className="actions">
          <button type="button" data-testid="contact-add" onClick={() => setAdding(true)}>
            Add a contact
          </button>
        </p>
      )}
      {can && adding && (
        <ContactForm
          prefix="contact-new"
          start={EMPTY_CONTACT}
          label="Add the contact"
          onCancel={() => setAdding(false)}
          onSubmit={async (values) => {
            const ok = await save((t) => addContact(t, accountId, crypto.randomUUID(), values), "The contact is added.");
            if (ok) setAdding(false);
            return ok;
          }}
        />
      )}
    </div>
  );
}

function PayeePart({ found, save, accountId }: { found: AccountSales; save: Save; accountId: string }) {
  const [editing, setEditing] = useState(false);
  const payee = found.payee;
  const start: PayeeValues = payee ?? { name: "", mail_name: "", mail_address: "", bank_details_needed: false };
  const [values, setValues] = useState(start);
  if (payee === null && !found.can_edit_payee) return null;
  return (
    <div data-testid="account-payee">
      <h3>Payee</h3>
      {payee ? (
        <dl className="facts">
          <dt>Payment name</dt>
          <dd data-testid="payee-name">{payee.name || "–"}</dd>
          <dt>Mail to</dt>
          <dd>
            {payee.mail_name || "–"}
            {payee.mail_address && <span className="sub"> · {payee.mail_address}</span>}
          </dd>
          <dt>Bank details</dt>
          <dd>
            {payee.bank_details_needed ? (
              <>
                <span className="chip-flag" data-testid="payee-bank-needed">
                  Bank details needed
                </span>{" "}
                Add them in the payment provider.
              </>
            ) : (
              "Not needed or done"
            )}
          </dd>
        </dl>
      ) : (
        <p data-testid="account-no-payee">No payee.</p>
      )}
      {found.can_edit_payee && !editing && (
        <p className="actions">
          <button
            type="button"
            data-testid="payee-edit"
            onClick={() => {
              setValues(start);
              setEditing(true);
            }}
          >
            {payee ? "Change the payee" : "Add a payee"}
          </button>
        </p>
      )}
      {found.can_edit_payee && editing && (
        <form
          data-testid="payee-form"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            if (await save((t) => setPayee(t, accountId, values), "The payee is saved.")) setEditing(false);
          }}
        >
          <label htmlFor="payee-name">Payment name (45 characters at most)</label>
          <input id="payee-name" maxLength={45} value={values.name} onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))} />
          <label htmlFor="payee-mail-name">Mail name</label>
          <input id="payee-mail-name" value={values.mail_name} onChange={(e) => setValues((v) => ({ ...v, mail_name: e.target.value }))} />
          <label htmlFor="payee-mail-address">Mail address</label>
          <input id="payee-mail-address" value={values.mail_address} onChange={(e) => setValues((v) => ({ ...v, mail_address: e.target.value }))} />
          <label className="check">
            <input type="checkbox" checked={values.bank_details_needed} onChange={(e) => setValues((v) => ({ ...v, bank_details_needed: e.target.checked }))} />
            Bank details needed. Never type bank numbers here: they go to the payment provider.
          </label>
          <div className="actions">
            <button className="primary" data-testid="payee-save">
              Save the payee
            </button>
            <button type="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Notes({ found, save, accountId, today }: { found: AccountSales; save: Save; accountId: string; today: string }) {
  const kinds: NoteKind[] = [...(found.can_add_sales_note ? (["sales"] as const) : []), ...(found.can_add_service_note ? (["service"] as const) : [])];
  const [all, setAll] = useState(false);
  const [kind, setKind] = useState<NoteKind>(kinds[0] ?? "service");
  const [day, setDay] = useState(today);
  const [body, setBody] = useState("");
  const notes = sortedNotes(found.notes);
  const shown = all ? notes : notes.slice(0, NOTES_FIRST);
  const line = (note: AccountNote) => (
    <li key={note.id} data-testid="note-row" data-kind={note.kind}>
      <span className="sub">
        {note.noted_on ? dayText(note.noted_on, true) : "No date"} · {NOTE_KIND_TEXT[note.kind]}
        {note.source === "import" ? " · from the sales import" : ` · ${note.created_by}`}
      </span>
      <div className="note-body">{note.body}</div>
    </li>
  );
  return (
    <div data-testid="account-notes">
      <h3>Notes</h3>
      {notes.length ? <ul className="notes">{shown.map(line)}</ul> : <p data-testid="account-no-notes">No notes.</p>}
      {!all && notes.length > NOTES_FIRST && (
        <p className="actions">
          <button type="button" data-testid="notes-all" onClick={() => setAll(true)}>
            Show all {notes.length} notes
          </button>
        </p>
      )}
      {kinds.length > 0 && (
        <form
          data-testid="note-form"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            const note = { id: crypto.randomUUID(), kind, noted_on: day || null, body: body.trim() };
            if (await save((t) => addNote(t, accountId, note), "The note is added.")) setBody("");
          }}
        >
          {kinds.length > 1 && (
            <>
              <label htmlFor="note-kind">Kind</label>
              <select id="note-kind" value={kind} onChange={(e) => setKind(e.target.value as NoteKind)}>
                {kinds.map((k) => (
                  <option key={k} value={k}>
                    {NOTE_KIND_TEXT[k]}
                  </option>
                ))}
              </select>
            </>
          )}
          <label htmlFor="note-day">Date</label>
          <input id="note-day" type="date" max={today} value={day} onChange={(e) => setDay(e.target.value)} />
          <label htmlFor="note-body">Note</label>
          <textarea id="note-body" data-testid="note-body" rows={2} maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="actions">
            <button data-testid="note-save" disabled={!body.trim()}>
              Add the note
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
